import { useState, useEffect } from 'react';
import '../styles/AdminSettings.css';
import '../styles/AdminAccounts.css';

const AdminSettings = () => {
    const [profile, setProfile] = useState({
        username: '',
        is_superadmin: false,
        can_manage_partners: false,
        can_manage_members: false,
        can_manage_events: false,
        can_manage_products: false,
        can_manage_projects: false,
        can_manage_reservations: false,
    });
    const [loading, setLoading] = useState(true);
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [message, setMessage] = useState({ type: '', text: '' });
    const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/me/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setProfile(data);
            }
        } catch (error) {
            console.error("Error fetching profile:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateUsername = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/me/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ username: profile.username })
            });
            if (response.ok) {
                setMessage({ type: 'success', text: "Nom d'utilisateur mis à jour !" });
                fetchProfile();
            } else {
                const data = await response.json();
                setMessage({ type: 'error', text: data.error || 'Erreur lors de la mise à jour.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Une erreur est survenue.' });
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        setPasswordMessage({ type: '', text: '' });

        if (!passwordData.newPassword) {
            setPasswordMessage({ type: 'error', text: 'Veuillez saisir un mot de passe.' });
            return;
        }
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas.' });
            return;
        }

        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/me/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ password: passwordData.newPassword })
            });
            if (response.ok) {
                setPasswordMessage({ type: 'success', text: 'Mot de passe modifié avec succès !' });
                setPasswordData({ newPassword: '', confirmPassword: '' });
                setTimeout(() => {
                    setShowPasswordForm(false);
                    setPasswordMessage({ type: '', text: '' });
                }, 1500);
            } else {
                const data = await response.json();
                setPasswordMessage({ type: 'error', text: data.error || 'Erreur lors de la mise à jour.' });
            }
        } catch (error) {
            setPasswordMessage({ type: 'error', text: 'Une erreur est survenue.' });
        }
    };

    const resetPasswordForm = () => {
        setShowPasswordForm(false);
        setPasswordData({ newPassword: '', confirmPassword: '' });
        setPasswordMessage({ type: '', text: '' });
    };

    const IconEye = () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
        </svg>
    );
    const IconEyeOff = () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
    );

    if (loading) return <div className="loading-state">Chargement...</div>;

    return (
        <div className="settings-container">

            {/* ── Carte Profil ── */}
            <div className="settings-card">
                <div className="section-title-group">
                    <div className="card-header-with-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f5c50a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                        </svg>
                        <h3>Mon Profil</h3>
                    </div>
                    <button
                        className="btn-add"
                        onClick={() => { if (showPasswordForm) resetPasswordForm(); else setShowPasswordForm(true); }}
                    >
                        {showPasswordForm ? 'Annuler' : 'Changer le mot de passe'}
                    </button>
                </div>
                <p className="card-subtitle">Gérez vos informations personnelles et la sécurité de votre compte.</p>

                {/* Formulaire mot de passe inline */}
                {showPasswordForm && (
                    <div className="admin-form-card">
                        <h4>Modifier le mot de passe</h4>
                        <form onSubmit={handleUpdatePassword}>
                            <div className="admin-form-grid">
                                <div className="form-column">
                                    <div className="form-group">
                                        <label>Nouveau mot de passe <span style={{ color: '#e74c3c' }}>*</span></label>
                                        <div className="password-input-wrapper">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={passwordData.newPassword}
                                                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                                placeholder="Entrez votre nouveau mot de passe"
                                                autoFocus
                                            />
                                            <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <IconEyeOff /> : <IconEye />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-column">
                                    <div className="form-group">
                                        <label>Confirmer le mot de passe <span style={{ color: '#e74c3c' }}>*</span></label>
                                        <div className="password-input-wrapper">
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={passwordData.confirmPassword}
                                                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                                placeholder="Confirmez le nouveau mot de passe"
                                            />
                                            <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                                {showConfirmPassword ? <IconEyeOff /> : <IconEye />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {passwordMessage.text && (
                                <div className={`message-alert ${passwordMessage.type}`}>
                                    {passwordMessage.type === 'success' ? '✓' : '⚠'} {passwordMessage.text}
                                </div>
                            )}

                            <div className="form-actions">
                                <button type="button" className="btn-cancel-edit" onClick={resetPasswordForm}>Annuler</button>
                                <button type="submit" className="btn-submit">Enregistrer le mot de passe</button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Formulaire nom d'utilisateur */}
                <form onSubmit={handleUpdateUsername} className="settings-form">
                    <div className="form-group">
                        <label>Nom d'utilisateur</label>
                        <input
                            type="text"
                            value={profile.username}
                            onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                            placeholder="Votre nom d'utilisateur"
                        />
                    </div>

                    <div className="role-info">
                        <span className={`role-badge ${profile.is_superadmin ? 'super' : 'standard'}`}>
                            {profile.is_superadmin ? 'Super-Administrateur' : 'Administrateur'}
                        </span>
                    </div>

                    {message.text && (
                        <div className={`message-alert ${message.type}`}>
                            {message.type === 'success' ? '✓' : '⚠'} {message.text}
                        </div>
                    )}

                    <button type="submit" className="save-btn">Enregistrer les modifications</button>
                </form>
            </div>

            {/* ── Carte Permissions ── */}
            <div className="permissions-card">
                <div className="card-header-with-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f5c50a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <h3>Mes Permissions</h3>
                </div>
                <p className="card-subtitle">Voici vos droits d'accès au système.</p>

                <div className="permissions-list">
                    <div className={`permission-tag ${profile.is_superadmin || profile.can_manage_partners ? 'active' : 'inactive'}`}>
                        Gestion des Partenaires
                    </div>
                    <div className={`permission-tag ${profile.is_superadmin || profile.can_manage_members ? 'active' : 'inactive'}`}>
                        Gestion des Membres
                    </div>
                    <div className={`permission-tag ${profile.is_superadmin || profile.can_manage_events ? 'active' : 'inactive'}`}>
                        Gestion des Activités
                    </div>
                    <div className={`permission-tag ${profile.is_superadmin || profile.can_manage_products ? 'active' : 'inactive'}`}>
                        Gestion des Produits
                    </div>
                    <div className={`permission-tag ${profile.is_superadmin || profile.can_manage_projects ? 'active' : 'inactive'}`}>
                        Gestion des Projets
                    </div>
                    <div className={`permission-tag ${profile.is_superadmin || profile.can_manage_reservations ? 'active' : 'inactive'}`}>
                        Gestion des Réservations
                    </div>
                    {profile.is_superadmin && (
                        <div className="permission-tag super">
                            Accès Total (Superadmin)
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminSettings;
